// ---------------------------------------------------------------------------
// EmailEditorChoiceModal
//
// Prompt shown when a user starts an email from scratch (no template picked):
// build with the drag-and-drop editor or the HTML editor. When a template is
// chosen instead, the editor follows the template's type and this is skipped.
// ---------------------------------------------------------------------------

import { LayoutTemplate, Code2, X } from 'lucide-react';
import type { EmailEditorType } from './editor-type';

export interface EmailEditorChoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChoose: (type: EmailEditorType) => void;
  title?: string;
  description?: string;
}

export default function EmailEditorChoiceModal({
  isOpen,
  onClose,
  onChoose,
  title = 'How do you want to build this email?',
  description = 'Choose an editor to start from scratch. You can always start from a template instead.',
}: EmailEditorChoiceModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5">
          <p className="mb-5 text-sm text-gray-600">{description}</p>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => onChoose('dnd')}
              className="group flex flex-col items-center gap-3 rounded-lg border border-gray-200 p-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600 group-hover:bg-blue-200">
                <LayoutTemplate className="h-6 w-6" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800">Drag &amp; Drop</div>
                <p className="mt-1 text-xs text-gray-500">
                  Build visually with blocks — columns, images, buttons and more.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => onChoose('html')}
              className="group flex flex-col items-center gap-3 rounded-lg border border-gray-200 p-6 text-center transition-colors hover:border-blue-400 hover:bg-blue-50"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-600 group-hover:bg-slate-200">
                <Code2 className="h-6 w-6" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800">HTML Editor</div>
                <p className="mt-1 text-xs text-gray-500">
                  Full control over the markup with live preview and code editing.
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
